import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, Globe } from "lucide-react";
import Logo from "./Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter`, { email });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <footer className="relative border-t border-line bg-panel" data-testid="site-footer">
      <div className="container-wide pt-20 pb-10">
        <div className="grid gap-14 lg:grid-cols-12">
          {/* Brand + newsletter */}
          <div className="lg:col-span-5">
            <Logo />
            <p className="mt-6 max-w-md font-sans text-sm leading-relaxed text-muted">
              Payment infrastructure built for ambitious and high-risk merchants.
              High-approval accounts, next-day funding, and a real-time dashboard —
              with no hidden fees.
            </p>
            <form onSubmit={subscribe} className="mt-8 max-w-md" data-testid="footer-newsletter-form">
              <div className="label">Stay informed</div>
              <div className="flex items-center gap-2 rounded-full border border-line bg-white/[0.02] p-1.5 focus-within:border-emerald/50">
                <input
                  type="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent px-4 py-2 font-sans text-sm text-cloud placeholder:text-faint focus:outline-none"
                  data-testid="footer-newsletter-email"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="shrink-0 rounded-full bg-emerald px-5 py-2 font-sans text-xs font-semibold text-[#04120b] transition-colors hover:bg-emerald-bright disabled:opacity-60"
                  data-testid="footer-newsletter-submit"
                >
                  {status === "loading" ? "..." : "Subscribe"}
                </button>
              </div>
              {status === "success" && (
                <p className="mt-3 font-mono text-xs text-emerald" data-testid="footer-newsletter-success">
                  ✓ Subscribed. Welcome aboard.
                </p>
              )}
              {status === "error" && (
                <p className="mt-3 font-mono text-xs text-red-400">
                  Something went wrong. Try again.
                </p>
              )}
            </form>
          </div>

          {/* Link columns */}
          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-7">
            <FooterCol title="Navigate" testid="footer-col-nav">
              <FooterLink to="/">Home</FooterLink>
              <FooterLink to="/about-us">About</FooterLink>
              <FooterLink to="/#solutions">Solutions</FooterLink>
              <FooterLink to="/#industries">Industries</FooterLink>
              <FooterLink to="/contact-us">Contact</FooterLink>
            </FooterCol>

            <FooterCol title="Platform" testid="footer-col-solutions">
              <FooterLink to="/#solutions">Merchant accounts</FooterLink>
              <FooterLink to="/#solutions">Alt payment methods</FooterLink>
              <FooterLink to="/#solutions">Chargeback defense</FooterLink>
              <FooterLink to="/#solutions">Compliance</FooterLink>
              <FooterLink to="/apply">Apply now</FooterLink>
            </FooterCol>

            <FooterCol title="Contact" testid="footer-col-contact">
              <li className="flex items-start gap-2.5">
                <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald" />
                <a
                  href="mailto:hello@merchantcore.us"
                  className="font-sans text-sm text-muted transition-colors hover:text-cloud"
                  data-testid="footer-email-link"
                >
                  hello@merchantcore.us
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald" />
                <a
                  href="https://merchantcore.us"
                  className="font-sans text-sm text-muted transition-colors hover:text-cloud"
                  data-testid="footer-domain-link"
                >
                  merchantcore.us
                </a>
              </li>
            </FooterCol>
          </div>
        </div>

        {/* Massive wordmark */}
        <div className="mt-16 select-none overflow-hidden border-t border-line pt-8">
          <div className="font-display text-[19vw] font-bold leading-[0.8] tracking-tight text-cloud/[0.04]">
            MerchantCore
          </div>
        </div>

        <div className="mt-6 flex flex-col items-center justify-between gap-3 border-t border-line pt-6 sm:flex-row">
          <p className="font-mono text-[11px] uppercase tracking-wide2 text-faint" data-testid="footer-copyright">
            © 2026 MerchantCore · merchantcore.us · All rights reserved.
          </p>
          <p className="font-mono text-[11px] uppercase tracking-wide2 text-faint">
            PCI DSS Level 1 · SOC 2
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children, testid }) {
  return (
    <div data-testid={testid}>
      <h4 className="font-mono text-[11px] font-medium uppercase tracking-ultra text-faint">
        {title}
      </h4>
      <ul className="mt-5 space-y-3.5">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }) {
  return (
    <li>
      <Link to={to} className="font-sans text-sm text-muted transition-colors hover:text-emerald">
        {children}
      </Link>
    </li>
  );
}
